import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from './AppIcon';
import { useAuth } from '../contexts/AuthContext';

const STATUS_LABEL = {
  completed: 'complete',
  archived: 'archived',
};

/**
 * Banner shown on site-scoped pages when the site is non-active.
 * Mirrors the server-side trigger — any write attempt against a
 * read-only site will be rejected by the database.
 *
 * Admin users see an inline "Open Site Settings →" CTA jumping to the
 * site's settings page so they can reactivate without having to navigate
 * back to a site detail first. Non-admin users see the text-only banner
 * (they can't reactivate anyway — the copy already directs them to
 * contact their admin via the "Site Settings" reference).
 */
const SiteReadOnlyBanner = ({ status, siteId, className = '' }) => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const isAdmin = userProfile?.role === 'admin';

  if (status !== 'completed' && status !== 'archived') return null;
  const label = STATUS_LABEL[status] || status;

  return (
    <div className={`flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 p-4 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-xl ${className}`}>
      <div className="flex items-start gap-3">
        <Icon name="Lock" size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">
            This site is {label} and is read-only.
          </p>
          <p className="text-xs text-amber-800 dark:text-amber-300 mt-0.5">
            History, reports, and documents remain viewable. Reactivate the site from Site Settings to resume work and billing.
          </p>
        </div>
      </div>
      {isAdmin && siteId && (
        <button
          onClick={() => navigate(`/sites/${siteId}/settings`)}
          className="text-sm font-semibold px-3 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white whitespace-nowrap flex-shrink-0 transition-colors"
        >
          Open Site Settings →
        </button>
      )}
    </div>
  );
};

export default SiteReadOnlyBanner;
